/**
 * Outline → grid: flatten a `d` string to closed polylines, seed the cells the outline passes through,
 * and mark inside cells by scanline parity. Pure, no allocation beyond the returned arrays.
 *
 * Grid convention (shared with edt.js and index.js): cell (x, y) is index `y * width + x`, its center sits at
 * `origin + (x + 0.5, y + 0.5) · texel` — the GL texel center for uv = (world − origin) / (texel · size).
 */
import { filled, foldRole, insideByRoles, subpathRoles } from '~/lib/outline/fill'
import { bezierToPoints, stringToRawPath } from '~/lib/svg/paths'

/** Outline samples per texel when seeding — sub-texel so no cell the outline crosses by more than a sliver is skipped. */
const SAMPLES_PER_TEXEL = 2

/**
 * One cubic → points appended to `out` (start point excluded). `threshold` is bezierToPoints' flatness:
 * (sum of control-point distances from the chord)² in stage units², so a deviation bound of `tol` is `tol²`.
 */
function flattenCubic(x1, y1, x2, y2, x3, y3, x4, y4, threshold, out) {
	const dx = x4 - x1
	const dy = y4 - y1
	const chord2 = dx * dx + dy * dy
	if (chord2 < 1e-12) {
		// A loop that returns to its start: bezierToPoints normalises by the chord, so split it first.
		if (x1 === x2 && x2 === x3 && x3 === x4 && y1 === y2 && y2 === y3 && y3 === y4) return
		const x12 = (x1 + x2) / 2
		const y12 = (y1 + y2) / 2
		const x23 = (x2 + x3) / 2
		const y23 = (y2 + y3) / 2
		const x34 = (x3 + x4) / 2
		const y34 = (y3 + y4) / 2
		const x123 = (x12 + x23) / 2
		const y123 = (y12 + y23) / 2
		const x234 = (x23 + x34) / 2
		const y234 = (y23 + y34) / 2
		const xm = (x123 + x234) / 2
		const ym = (y123 + y234) / 2
		flattenCubic(x1, y1, x12, y12, x123, y123, xm, ym, threshold, out)
		flattenCubic(xm, ym, x234, y234, x34, y34, x4, y4, threshold, out)
		return
	}
	const d2 = Math.abs((x2 - x4) * dy - (y2 - y4) * dx)
	const d3 = Math.abs((x3 - x4) * dy - (y3 - y4) * dx)
	if ((d2 + d3) * (d2 + d3) <= threshold * chord2) {
		// Straight already (every `L` arrives as a cubic with controls on the chord): one edge, no midpoint.
		out.push(x4, y4)
		return
	}
	const pts = bezierToPoints(x1, y1, x2, y2, x3, y3, x4, y4, threshold)
	for (let i = 2; i < pts.length; i++) out.push(pts[i])
}

/**
 * `d` → closed polylines, one per subpath, as flat [x0, y0, x1, y1, …] arrays without the closing repeat.
 * `tolerance` is the max distance (stage units) between a curve and its flattening. Every subpath is treated
 * as closed — an outline is closed by contract, and stringToRawPath drops a `Z` whose gap is under 0.5 px.
 *
 * `info`, when given, receives the edge → segment map (flow seeds, U5a): `info.segment` has one entry per flattened
 * edge in `edgesOf` order, the outline segment it came from — segment i runs anchor i → anchor i + 1 of the FIRST
 * subpath, the closing segment included, numbered as `~/lib/outline#anchorsOf` counts anchors — and −1 for edges of
 * any later subpath (holes carry no segment). `info.segments` is the first subpath's segment count (0 if it flattened
 * to nothing).
 */
export function flattenPath(d, tolerance, info) {
	if (info) {
		info.segment = new Int32Array(0)
		info.segments = 0
	}
	if (typeof d !== 'string' || !/[mM]/.test(d)) return []
	const raw = stringToRawPath(d)
	const roles = subpathRoles(raw)
	const threshold = tolerance * tolerance
	const polys = []
	const segOf = info ? [] : null
	// False once the latest fill flattened to nothing: its holes cut nothing.
	let fillKept = false
	for (let r = 0; r < raw.length; r++) {
		const seg = raw[r]
		if (seg.length < 8) continue
		if (roles[r] > 0) fillKept = false
		const out = [seg[0], seg[1]]
		// Per point after the first, the cubic that drew it — the segment of the edge ending there.
		const by = segOf ? [] : null
		for (let i = 2; i + 5 < seg.length; i += 6) {
			flattenCubic(
				seg[i - 2],
				seg[i - 1],
				seg[i],
				seg[i + 1],
				seg[i + 2],
				seg[i + 3],
				seg[i + 4],
				seg[i + 5],
				threshold,
				out
			)
			if (by) while (by.length < out.length / 2 - 1) by.push((i - 2) / 6)
		}
		const n = out.length
		const shut = n > 2 && Math.abs(out[n - 2] - out[0]) < 1e-9 && Math.abs(out[n - 1] - out[1]) < 1e-9
		if (shut) out.length = n - 2
		if (out.length < 6) continue
		if (roles[r] > 0) fillKept = true
		out.role = roles[r] > 0 || fillKept ? roles[r] : 0
		polys.push(out)
		if (!segOf) continue
		const cubics = (seg.length - 2) / 6
		// anchorsOf's rule: the last anchor is the first again when it lands on it exactly, or within stringToRawPath's
		// 0.5 px `Z` tolerance; then the last cubic is the closing segment, else an implicit line is one more.
		const lx = seg[seg.length - 2]
		const ly = seg[seg.length - 1]
		const closes =
			(lx === seg[0] && ly === seg[1]) ||
			(seg.closed && Math.abs(lx - seg[0]) <= 0.5 && Math.abs(ly - seg[1]) <= 0.5)
		const segments = closes ? cubics : cubics + 1
		const first = r === 0 // anchorsOf reads raw[0] only
		if (first) info.segments = segments
		// Edge j runs point j → j + 1; the last edge wraps to point 0. Shut: that edge ended on the dropped repeat, so
		// `by` already holds it. Open: the wrap is the closing segment.
		const points = out.length / 2
		for (let j = 0; j < points; j++) segOf.push(first ? (j < by.length ? by[j] : segments - 1) : -1)
	}
	if (info) info.segment = Int32Array.from(segOf)
	return polys
}

/** Control-hull bounds of a `d` string's raw path — cheap, and never smaller than the curve. */
export function hullBounds(d) {
	if (typeof d !== 'string' || !/[mM]/.test(d)) return null
	return boundsOf(stringToRawPath(d))
}

/** [minX, minY, maxX, maxY] over flat point arrays, or null when empty. */
export function boundsOf(polys) {
	let minX = Infinity
	let minY = Infinity
	let maxX = -Infinity
	let maxY = -Infinity
	for (const p of polys) {
		for (let i = 0; i + 1 < p.length; i += 2) {
			if (p[i] < minX) minX = p[i]
			if (p[i] > maxX) maxX = p[i]
			if (p[i + 1] < minY) minY = p[i + 1]
			if (p[i + 1] > maxY) maxY = p[i + 1]
		}
	}
	return minX <= maxX && minY <= maxY ? [minX, minY, maxX, maxY] : null
}

/** Floats per edge in `edgesOf` output: start x, y, delta x, y, 1 / length² (0 for a zero-length edge). */
export const EDGE_STRIDE = 5

/** Closed polylines → one Float64Array of edges (EDGE_STRIDE floats each), each polyline wrapping to its start. */
export function edgesOf(polys) {
	let count = 0
	for (const p of polys) count += p.length / 2
	const edges = new Float64Array(count * EDGE_STRIDE)
	let k = 0
	for (const p of polys) {
		const n = p.length / 2
		for (let i = 0; i < n; i++) {
			const j = (i + 1) % n
			const dx = p[2 * j] - p[2 * i]
			const dy = p[2 * j + 1] - p[2 * i + 1]
			const len2 = dx * dx + dy * dy
			edges[k++] = p[2 * i]
			edges[k++] = p[2 * i + 1]
			edges[k++] = dx
			edges[k++] = dy
			edges[k++] = len2 > 0 ? 1 / len2 : 0
		}
	}
	return edges
}

/**
 * Chain links for `edgesOf` output: `next[e]` / `prev[e]` are the edges sharing e's end / start in the same polyline.
 * @returns {{ next: Int32Array, prev: Int32Array }}
 */
export function edgeLinks(polys) {
	let count = 0
	for (const p of polys) count += p.length / 2
	const next = new Int32Array(count)
	const prev = new Int32Array(count)
	let base = 0
	for (const p of polys) {
		const n = p.length / 2
		for (let i = 0; i < n; i++) {
			next[base + i] = base + ((i + 1) % n)
			prev[base + i] = base + ((i + n - 1) % n)
		}
		base += n
	}
	return { next, prev }
}

/** Squared distance from (px, py) to edge `e` of an edges array. */
export function edgeDist2(edges, e, px, py) {
	const o = e * EDGE_STRIDE
	const ax = edges[o]
	const ay = edges[o + 1]
	const dx = edges[o + 2]
	const dy = edges[o + 3]
	let t = ((px - ax) * dx + (py - ay) * dy) * edges[o + 4]
	t = t < 0 ? 0 : t > 1 ? 1 : t
	const ex = ax + t * dx - px
	const ey = ay + t * dy - py
	return ex * ex + ey * ey
}

/**
 * Seed cells: sample every edge at sub-texel spacing; each cell a sample lands in keeps the edge nearest its center.
 * Returns Int32Array of edge indices per cell, −1 where the outline does not pass. `seed` / `best` are optional
 * reusable buffers of at least width · height cells.
 */
export function seedEdges(edges, grid, seed, best) {
	const { width, height, texel } = grid
	const [ox, oy] = grid.origin
	const n = width * height
	seed = seed ? seed.fill(-1, 0, n) : new Int32Array(n).fill(-1)
	best = best || new Float64Array(n)
	const step = texel / SAMPLES_PER_TEXEL
	const edgeCount = edges.length / EDGE_STRIDE
	for (let e = 0; e < edgeCount; e++) {
		const o = e * EDGE_STRIDE
		const ax = edges[o]
		const ay = edges[o + 1]
		const dx = edges[o + 2]
		const dy = edges[o + 3]
		const n = Math.max(1, Math.ceil(Math.sqrt(dx * dx + dy * dy) / step))
		for (let s = 0; s <= n; s++) {
			const t = s / n
			// Clamped: an outline on the grid's far edge (bounds max, no padding) belongs to the last cell.
			const cx = Math.min(width - 1, Math.max(0, Math.floor((ax + t * dx - ox) / texel)))
			const cy = Math.min(height - 1, Math.max(0, Math.floor((ay + t * dy - oy) / texel)))
			const c = cy * width + cx
			if (seed[c] === e) continue
			const d2 = edgeDist2(edges, e, ox + (cx + 0.5) * texel, oy + (cy + 0.5) * texel)
			if (seed[c] < 0 || d2 < best[c]) {
				seed[c] = e
				best[c] = d2
			}
		}
	}
	return seed
}

/**
 * Inside mask by even-odd scanline parity at cell centers. Edges are bucketed by the rows they span (half-open in y,
 * from one shared expression per vertex, so a vertex on a scanline is counted exactly once), O(edges + crossings).
 * Returns Uint8Array, 1 inside; `inside` is an optional reusable buffer of at least width · height cells.
 */
export function parityMask(edges, grid, inside) {
	const { width, height, texel } = grid
	const [ox, oy] = grid.origin
	const edgeCount = edges.length / EDGE_STRIDE
	const rowOf = v => Math.min(height, Math.max(0, Math.ceil((v - oy) / texel - 0.5)))
	const start = new Int32Array(height + 1)
	for (let e = 0; e < edgeCount; e++) {
		const ay = edges[e * EDGE_STRIDE + 1]
		const by = ay + edges[e * EDGE_STRIDE + 3]
		const r1 = rowOf(Math.max(ay, by))
		for (let r = rowOf(Math.min(ay, by)); r < r1; r++) start[r + 1]++
	}
	for (let r = 0; r < height; r++) start[r + 1] += start[r]
	const xs = new Float64Array(start[height])
	const fill = start.slice(0, height)
	for (let e = 0; e < edgeCount; e++) {
		const o = e * EDGE_STRIDE
		const ax = edges[o]
		const ay = edges[o + 1]
		const bx = ax + edges[o + 2]
		const by = ay + edges[o + 3]
		const r1 = rowOf(Math.max(ay, by))
		for (let r = rowOf(Math.min(ay, by)); r < r1; r++) {
			const yc = oy + (r + 0.5) * texel
			xs[fill[r]++] = ax + ((yc - ay) * (bx - ax)) / (by - ay)
		}
	}
	inside = inside ? inside.fill(0, 0, width * height) : new Uint8Array(width * height)
	for (let r = 0; r < height; r++) {
		const row = xs.subarray(start[r], start[r + 1]).sort()
		for (let i = 0; i + 1 < row.length; i += 2) {
			const x0 = Math.max(0, Math.ceil((row[i] - ox) / texel - 0.5))
			const x1 = Math.min(width, Math.ceil((row[i + 1] - ox) / texel - 0.5))
			if (x1 > x0) inside.fill(1, r * width + x0, r * width + x1)
		}
	}
	return inside
}

/** Whether (px, py) is filled by `flattenPath`'s polylines, by their `role`s. */
function filledAt(polys, roles, px, py) {
	return insideByRoles(roles, i => {
		const p = polys[i]
		let inside = false
		for (let a = 0, n = p.length, b = n - 2; a < n; b = a, a += 2) {
			const ay = p[b + 1]
			const by = p[a + 1]
			if (ay > py !== by > py && px < p[b] + ((py - ay) * (p[a] - p[b])) / (by - ay)) inside = !inside
		}
		return inside
	})
}

/** Edges sampled per polyline when testing whether it bounds the fill. */
const BOUND_SAMPLES = 8

/** Drops a polyline with the fill on both sides of it (or neither), so its edges put no false zero of r inside; `info.segment` is cut to match. */
export function boundingPolys(polys, info) {
	if (polys.length < 2) return polys
	const roles = Int8Array.from(polys, p => p.role)
	const bounds = p => {
		const n = p.length / 2
		const step = Math.max(1, Math.floor(n / BOUND_SAMPLES))
		for (let i = 0; i < n; i += step) {
			const j = (i + 1) % n
			const dx = p[2 * j] - p[2 * i]
			const dy = p[2 * j + 1] - p[2 * i + 1]
			const len = Math.hypot(dx, dy)
			if (!len) continue
			const eps = Math.min(0.05, len / 4) / len
			const mx = p[2 * i] + dx / 2
			const my = p[2 * i + 1] + dy / 2
			if (
				filledAt(polys, roles, mx - dy * eps, my + dx * eps) !==
				filledAt(polys, roles, mx + dy * eps, my - dx * eps)
			)
				return true
		}
		return false
	}
	const keep = polys.map(bounds)
	if (keep.every(Boolean)) return polys
	if (info?.segment.length) {
		const segment = []
		let base = 0
		polys.forEach((p, k) => {
			if (keep[k]) for (let e = 0; e < p.length / 2; e++) segment.push(info.segment[base + e])
			base += p.length / 2
		})
		info.segment = Int32Array.from(segment)
	}
	return polys.filter((_, k) => keep[k])
}

/**
 * Inside mask by the shape's fill rule (`~/lib/outline/fill`) over `flattenPath`'s polylines: each subpath by parity, then islands and holes as PixiShape paints them. One polyline is `parityMask` of `edges` (its `edgesOf`) as is.
 */
export function fillMask(polys, edges, grid, inside) {
	if (polys.length < 2) return parityMask(edges, grid, inside)
	const n = grid.width * grid.height
	inside = inside ? inside.fill(0, 0, n) : new Uint8Array(n)
	const sub = new Uint8Array(n)
	for (const p of polys) {
		if (!p.role) continue
		parityMask(edgesOf([p]), grid, sub)
		for (let c = 0; c < n; c++) inside[c] = foldRole(inside[c], p.role, sub[c])
	}
	for (let c = 0; c < n; c++) inside[c] = filled(inside[c])
	return inside
}
